import { css, cva } from '@styled-system/css';
import { BarChart3, Layers, Pencil, Radar, Search, ShoppingBag, Trash2, TriangleAlert, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AnalysisComputePending } from '@/features/analysisPage/components/AnalysisComputePending';
import { AnalysisSegmentProvider, useAnalysisSegment } from '@/features/analysisPage/contexts/AnalysisSegmentContext';
import { useAnalysisPageData } from '@/features/analysisPage/hooks/useAnalysisPageData';
import { useAnalysisTabRedirect } from '@/features/analysisPage/hooks/useAnalysisTabRedirect';
import { AnalysisPageNiches } from '@/features/analysisPageNiches/components/AnalysisPageNiches';
import { AnalysisPageSellers } from '@/features/analysisPageSellers/components/AnalysisPageSellers';
import { AnalysisPageSellsHist } from '@/features/analysisPageSellsHist/components/AnalysisPageSellsHist';
import { AnalysisPageStatistics } from '@/features/analysisPageStatistics/components/AnalysisPageStatistics';
import { useAnalysisStatistics } from '@/features/analysisPageStatistics/hooks';
import { SearchModal } from '@/features/productSearch/components/SearchModal';
import {
  useVestiaireCountryIsoCodes,
  useVestiaireMaterialLevels,
} from '@/features/productSearch/vestiaire/useVestiaireSearchUrl';
import { useCreateVeille, useUserVeilles } from '@/features/veille/useVeilleApi';
import {
  AnalysisIntentChip,
  AnalysisSimilarityDegradedChip,
  AnalysisVintedLink,
} from '@/shared/components/analysis/AnalysisChips';
import { AnalysisEngineBanner } from '@/shared/components/analysis/AnalysisEngineBanner';
import { AnalysisKeyStats } from '@/shared/components/analysis/AnalysisKeyStats';
import { PlatformBadge } from '@/shared/components/analysis/PlatformBadge';
import { SegmentBar } from '@/shared/components/analysis/SegmentBar';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Button } from '@/shared/components/ui/Button';
import { Message } from '@/shared/components/ui/Message';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useGetVestiaireCatalogTreeQuery } from '@/shared/hooks/queries/generated';
import { useUserLanguage } from '@/shared/hooks/useUserLanguage';
import { analysisCriteriaFilter, isVestiaireAnalysis } from '@/shared/utils/analysis/platform';
import { parsedFilterToCriteria } from '@/shared/utils/analysis/relaunchCriteria';
import { analysisExternalUrl } from '@/shared/utils/analysis/vestiaireAnalysisUrl';

// ============================================================================
// Styles
// ============================================================================

const page = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '6',
  width: '100%',
});

// Navigation principale de l'analyse — sélecteur segmenté marqué (plus gros
// que les sous-sélecteurs de contenu pour bien s'en distinguer).
const tabBar = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1',
  padding: '2',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
  width: 'fit-content',
});

const tabButton = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    paddingY: '3',
    paddingX: '7',
    fontSize: 'body',
    fontFamily: 'sans',
    fontWeight: 'medium',
    borderRadius: 'lg',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    background: 'transparent',
    color: 'text.secondary',
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease, box-shadow 120ms ease',
    whiteSpace: 'nowrap',
  },
  variants: {
    active: {
      true: {
        bg: 'surface.default',
        color: 'text.primary',
        fontWeight: 'semibold',
        borderColor: 'border.default',
        boxShadow: 'xs',
      },
      false: {
        _hover: { color: 'text.primary' },
      },
    },
  },
});

const centered = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingY: '24',
});

const headerThumb = css({
  width: '32px',
  height: '32px',
  borderRadius: 'md',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const headerThumbFallback = css({
  width: '32px',
  height: '32px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

// ============================================================================
// Page
// ============================================================================

export const AnalysisDetailPage: FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const pageData = useAnalysisPageData(id);

  return (
    <AnalysisSegmentProvider
      lockFilter={analysisCriteriaFilter(pageData.analysisHeaderData)}
      platform={pageData.analysisHeaderData?.platform ?? 'vinted'}
    >
      <AnalysisDetailPageContent id={id} pageData={pageData} />
    </AnalysisSegmentProvider>
  );
};

interface AnalysisDetailPageContentProps {
  id: string;
  pageData: ReturnType<typeof useAnalysisPageData>;
}

const AnalysisDetailPageContent: FC<AnalysisDetailPageContentProps> = ({ id, pageData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const lang = i18n.language ?? 'fr';

  // La même page sert les analyses produit ET vendeur — seule la base des
  // routes (et le libellé du fil d'Ariane) change.
  const isVendor = location.pathname.startsWith('/analysis/vendors');
  const basePath = isVendor ? 'vendors' : 'products';
  const listTitle = isVendor ? t('vendorSearch.title') : t('productSearch.title');
  const variant = isVendor ? ('vendor' as const) : ('product' as const);

  const { analysisHeaderData, activeTab, handleRename, handleDelete, isLoading, error, isUpdating } = pageData;

  const { segment, isActive: segmentActive } = useAnalysisSegment();

  // Création asynchrone : tant que le calcul n'est pas terminé, AUCUNE query
  // de données ne doit partir — nos caches sont immuables (staleTime Infinity)
  // et figeraient des résultats vides/partiels pour toujours. Tant que
  // l'analyse n'est PAS CHARGÉE, l'état est inconnu : on ne présume jamais
  // ready (sinon les stats d'une analyse en calcul partent trop tôt et
  // cachent du vide définitivement — reload obligatoire pour s'en sortir).
  const computeState = analysisHeaderData?.computeState ?? 'ready';
  const computeReady = analysisHeaderData != null && computeState === 'ready';

  // Fin de calcul observée en direct : on laisse la barre de progression se
  // remplir (animation ~1 s) avant de révéler les résultats — les queries de
  // données partent déjà pendant l'animation (préchargement).
  const [revealPending, setRevealPending] = useState(false);
  const wasComputingRef = useRef(false);
  useEffect(() => {
    if (computeState === 'queued' || computeState === 'running') {
      wasComputingRef.current = true;
      return;
    }
    if (computeState === 'ready' && wasComputingRef.current) {
      wasComputingRef.current = false;
      setRevealPending(true);
      const handle = window.setTimeout(() => setRevealPending(false), 1100);
      return () => window.clearTimeout(handle);
    }
  }, [computeState]);

  // Stats clés du header — recalculées PAR LE SERVEUR quand le segment est
  // actif ; la version non segmentée (cache immuable) donne le total du ratio.
  const { data: statsData } = useAnalysisStatistics({ analysisId: computeReady ? id : undefined });
  const { data: segmentedQueryStats, loading: statsLoading } = useAnalysisStatistics({
    analysisId: computeReady ? id : undefined,
    segment,
  });
  const segmentedStats = segmentedQueryStats;

  useAnalysisTabRedirect(id, analysisHeaderData);

  const analysis = analysisHeaderData;
  const isVestiaire = isVestiaireAnalysis(analysis);

  // Lien externe « Voir sur Vestiaire Collective » : recalculé depuis vestiaireFilter pour une
  // analyse VC de CATALOGUE (cf analysisExternalUrl) ; arbre et pays mêmes options de cache que
  // useVestiaireSearchForm/useVestiaireSearchUrl (staleTime Infinity / 1h), matières par la même
  // liste complète que le panneau, tous coupés hors VC.
  const { language } = useUserLanguage();
  const { data: treeData } = useGetVestiaireCatalogTreeQuery(undefined, {
    enabled: isVestiaire,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  const countryIsoCodes = useVestiaireCountryIsoCodes(isVestiaire);
  const materialLevels = useVestiaireMaterialLevels(isVestiaire);
  const externalUrl = useMemo(() => {
    if (!analysis) return undefined;
    // `analysis.type` (AnalysisHeaderData) est l'énum FRONT product/vendor/public
    // (mapBackendAnalysisTypeToFrontendType, shared/api/mappers/analysis.ts), PAS l'AnalysisType
    // GraphQL catalog/profile attendu par analysisExternalUrl — 'vendor' correspond exactement à
    // 'profile', tout le reste (dont le défaut 'product') à 'catalog'.
    // Arbre absent (chargement en cours, requête en échec, ou simplement hors VC) : `null`, pas `[]`
    // — analysisExternalUrl replie alors sur pageUrl au lieu de générer un lien aux catégories omises.
    // Même règle pour les pays et les matières (`null` tant que leur liste n'est pas chargée).
    return analysisExternalUrl(
      { ...analysis, type: analysis.type === 'vendor' ? 'profile' : 'catalog' },
      { language, tree: treeData?.getVestiaireCatalogTree ?? null, countryIsoCodes, materialLevels },
    );
  }, [analysis, language, treeData, countryIsoCodes, materialLevels]);

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(lang.startsWith('en') ? 'en-GB' : 'fr-FR');
  };

  // ─── Slots du header de page (la barre sticky de la card) ───────────────
  const headerStats = analysis ? (
    <>
      <AnalysisIntentChip analysis={analysis} />
      <AnalysisSimilarityDegradedChip degraded={analysis.similarityDegraded} />
      <span>{t('analysisDetail.createdOn', { date: formatDate(analysis.createdAt) })}</span>
      {statsData?.totalProductsAnalyzed != null && (
        <>
          <span>·</span>
          <span>
            {/* Segment actif : ratio « articles du segment / articles du périmètre » */}
            {segmentActive && segmentedStats?.totalProductsAnalyzed != null
              ? t('analysisDetail.articlesRatio', {
                  count: segmentedStats.totalProductsAnalyzed,
                  total: statsData.totalProductsAnalyzed,
                })
              : t('analysisDetail.articles', { count: statsData.totalProductsAnalyzed })}
          </span>
        </>
      )}
    </>
  ) : undefined;

  // Mise sous veille depuis l'analyse — la veille prend ses PROPRES critères
  // (le serveur ne recopie rien) : parsedFilter -> criteria pour un produit,
  // id membre extrait de l'URL pour un vendeur. sourceAnalysisId = provenance.
  const { veilles } = useUserVeilles();
  const { createVeille } = useCreateVeille();
  const existingVeille = analysis ? veilles.find((veille) => veille.sourceAnalysisId === analysis.id) : undefined;
  const handleWatch = async () => {
    // La veille n'existe pas sur VC : createVeille lirait `parsedFilter`, toujours null sur VC.
    if (!analysis || isVestiaire) return;
    if (existingVeille) {
      navigate(`/veille/${existingVeille.id}`);
      return;
    }
    const input: Parameters<typeof createVeille>[0] = { name: analysis.name, sourceAnalysisId: analysis.id };
    if (variant === 'vendor') {
      const memberId = /\/member\/(\d+)/.exec(analysis.pageUrl ?? '')?.[1];
      if (!memberId) {
        window.alert(t('veille.vendorUnknown'));
        return;
      }
      input.sellerVintedId = memberId;
    } else {
      input.criteria = parsedFilterToCriteria(analysis.parsedFilter) as Record<string, unknown>;
    }
    const result = await createVeille(input);
    if (result.success) {
      navigate(`/veille/${result.veilleId}`);
    } else {
      window.alert(result.error);
    }
  };

  const headerActions = analysis ? (
    <>
      {/* La veille n'existe pas sur VC (pas de source pour la rejouer) */}
      {!isVestiaire && (
        <Button
          variant="ghost"
          size="sm"
          icon={<Radar size={13} strokeWidth={1.75} />}
          handleClick={handleWatch}
          showLoader
          disabled={!computeReady}
        >
          {existingVeille ? t('veille.watching') : t('veille.watch')}
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        icon={<Pencil size={13} strokeWidth={1.75} />}
        onClick={() => setIsEditOpen(true)}
        disabled={isUpdating}
      >
        {t('analysisDetail.edit')}
      </Button>
      <Button
        variant="danger"
        size="sm"
        icon={<Trash2 size={13} strokeWidth={1.75} />}
        handleClick={handleDelete}
        showLoader
      >
        {t('analysisDetail.delete')}
      </Button>
    </>
  ) : undefined;

  const tabs: { value: string; label: string; icon: ReactNode }[] = [
    { value: 'history', label: t('analysisDetail.tabSales'), icon: <ShoppingBag size={15} strokeWidth={1.75} /> },
    { value: 'niches', label: t('analysisDetail.tabNiches'), icon: <Layers size={15} strokeWidth={1.75} /> },
    { value: 'statistics', label: t('analysisDetail.tabStatistics'), icon: <BarChart3 size={15} strokeWidth={1.75} /> },
    {
      value: 'sellers',
      label: isVendor ? t('analysisDetail.tabSimilarSellers') : t('analysisDetail.tabSellers'),
      icon: <Users size={15} strokeWidth={1.75} />,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'statistics':
        return <AnalysisPageStatistics variant={variant} />;
      case 'niches':
        return <AnalysisPageNiches variant={variant} />;
      case 'sellers':
        return <AnalysisPageSellers variant={variant} />;
      case 'history':
        return <AnalysisPageSellsHist variant={variant} />;
      default:
        return null;
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={centered}>
          <Spinner size={28} />
        </div>
      );
    }

    if (error || !analysis) {
      return (
        <div className={centered}>
          <Message type="error" bordered>
            {error || t('analysisDetail.notFound')}
          </Message>
        </div>
      );
    }

    // Calcul serveur en cours ou échoué : écran d'attente à la place des
    // onglets — les composants de données ne montent pas (caches préservés).
    // revealPending : le calcul vient de finir, la barre se remplit à 100 %
    // avant de basculer sur les résultats.
    if (!computeReady || revealPending) {
      return (
        <>
          {/* Une barre qui stagne a souvent sa cause côté moteur : la dire
              ici, c'est la différence entre « ça n'avance pas » et « le
              calcul reprendra tout seul, rien n'est perdu ». Rend `null`
              quand le moteur est sain. */}
          <AnalysisEngineBanner />
          <AnalysisComputePending
            state={computeState}
            done={revealPending && computeReady}
            createdAt={analysis.createdAt}
            queuePosition={analysis.queuePosition}
            publishedCount={analysis.publishedCount}
            computeError={analysis.computeError}
            onCancel={handleDelete}
          />
        </>
      );
    }

    return (
      <>
        {/* Recherche très large : calcul coupé au dernier million d'articles
            lus (~5 h de flux) — les chiffres décrivent cette fenêtre, pas le
            marché entier. Un compteur bas ne le dit pas : seul ce booléen. */}
        {analysis.scanTruncated && (
          <div
            className={css({
              display: 'flex',
              gap: '3',
              alignItems: 'flex-start',
              fontSize: 'caption',
              color: 'warning.default',
              bg: 'warning.soft',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'warning.border',
              borderRadius: 'md',
              paddingY: '3',
              paddingX: '4',
              lineHeight: 1.55,
            })}
          >
            <TriangleAlert size={13} strokeWidth={1.75} className={css({ flexShrink: 0, marginTop: '0.5' })} />
            <span>{t('analysisDetail.scanTruncated')}</span>
          </div>
        )}

        {/* Barre de segment — recalcule tous les chiffres de la page.
            Vendeur : seulement état + prix. */}
        <SegmentBar dimensions={isVendor ? ['condition', 'price'] : undefined} />

        {/* Stats clés — toujours visibles, sous le header de page */}
        <AnalysisKeyStats data={segmentedStats} loading={statsLoading} showScore={!isVendor} />

        <div className={tabBar}>
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={tabButton({ active: activeTab === tab.value })}
              onClick={() => navigate(`/analysis/${basePath}/${id}/tab/${tab.value}`)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Séparation nette entre la navigation et le contenu de l'onglet */}
        <div className={css({ height: '1px', bg: 'border.default' })} />

        <div className={css({ paddingTop: '6' })}>{renderTabContent()}</div>
      </>
    );
  };

  return (
    <AppLayout
      title={analysis?.name ?? listTitle}
      breadcrumbs={[{ title: listTitle, onClick: () => navigate(`/analysis/${basePath}`) }]}
      headerLeading={
        analysis ? (
          // Recherche vendeur : `imageUrl` est l'AVATAR du vendeur (API, 2026-10-01) — rendu rond comme
          // tout avatar, avec repli sur les initiales s'il manque ou ne charge pas.
          isVendor ? (
            <Avatar name={analysis.name} src={analysis.imageUrl ?? undefined} size="md" />
          ) : analysis.imageUrl ? (
            <img src={analysis.imageUrl} alt="" className={headerThumb} />
          ) : (
            <span className={headerThumbFallback}>
              <Search size={14} strokeWidth={1.75} />
            </span>
          )
        ) : undefined
      }
      headerTitleExtras={
        analysis ? (
          <>
            <PlatformBadge platform={analysis.platform} size="md" />
            <AnalysisVintedLink pageUrl={externalUrl} platform={analysis.platform} />
          </>
        ) : undefined
      }
      headerStats={headerStats}
      headerActions={headerActions}
    >
      <div className={page}>{renderBody()}</div>

      {analysis && (
        <SearchModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          mode="edit"
          initialName={analysis.name}
          initialUrl={analysis.pageUrl}
          initialParsedFilter={analysis.parsedFilter}
          initialPlatform={analysis.platform}
          initialVestiaireFilter={analysis.vestiaireFilter ?? null}
          onSubmit={async ({ title }) => {
            await handleRename(title);
          }}
        />
      )}
    </AppLayout>
  );
};
